// This component houses Sample records for selected table from dataset
// This sample records can be view as
// 	- Full table with all fields and values
// 	- Just column names of table

// Table columns are draggable. These dragged table columns are then dropped into dropzones
import { NotificationDialog } from '../CommonFunctions/DialogComponents';

import { useEffect, useMemo, useRef, useState } from 'react';
import { IndTabRilePropsTablesForSelectedDatasets } from '../../redux/TabTile/TabTilePropsInterfaces';
import { Box } from './Box';
import { TableContainer, Table, TableHead, TableRow, TableBody, TableCell } from '@mui/material';
import {
  deleteSavedCalculation,
  editSavedCalculation,
  resetFetchRowLevelCalc,
} from '../../redux/Calculations/CalculationsActions';
import FetchData from '../ServerCall/FetchData';
import { deleteItemInChartProp } from '../../redux/ChartPoperties/ChartPropertiesActions';
import { deleteItemfromChartFilter } from '../../redux/ChartFilterGroup/ChartFilterGroupStateActions';
import { getKeys, getRowsData } from './DisplayTableHelperFunctions';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux';
import { useDispatch } from 'react-redux';
import LoadingPopover from '../CommonFunctions/PopOverComponents/LoadingPopover';
import { ChartPropChartAxes } from '../../redux/ChartPoperties/ChartPropertiesInterfaces';
import { TAlert } from '../../types/types';
import { IGetMetaDataColumns } from '../../api/types/dataset';
import { checkIfMissingColumn, updateSampleRecords } from '../CommonFunctions/CommonFunctions';
import { getColumnTypes, getTableData } from '../../api';
import { removeCalculationId } from '../../redux/Parameters/ParametersAction';
import { ICurrentCalculationSession } from '../../redux/Calculations/CurrentCalculationSessionInterface';

interface DisplayTableProps {
  dsId: string;
  table: string;
  recordLoaded: boolean;
}
interface IFieldDataProps {
  fieldname: string;
  displayname: string;
  dataType: string;
  tableId: string;
  isAggregated?: boolean;
}
const DisplayTable = ({
  // props
  dsId,
  table,
  recordLoaded,
}: DisplayTableProps) => {
  const { tabTileProps, calculations, chartProperties, chartGroup, parameters } = useSelector(
    (state: RootState) => ({
      tabTileProps: state.tabTileProps,
      calculations: state.calculations,
      chartProperties: state.chartProperties,
      chartGroup: state.chartFilterGroup,
      parameters: state.parameters,
    }),
  );
  const [columnsData, setColumnsData] = useState<IFieldDataProps[]>([]);
  const [alert, setAlert] = useState<TAlert | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const savedCalculations = calculations?.savedCalculations;

  const dispatch = useDispatch();
  const tableRef = useRef<any | HTMLDivElement>(null);
  const didMountRef = useRef(false);

  const propKey = `${tabTileProps.selectedTabId}.${tabTileProps.selectedTileId}`;

  const [warningCalculation, setWarningCalculation] = useState<{
    [key: string]: boolean;
  }>({});

  const selectedDs = useMemo(() => {
    return chartProperties?.properties[propKey]?.selectedDs;
  }, [
    chartProperties?.properties[propKey]?.selectedDs,
    chartProperties?.properties[propKey]?.oldSelectedDs,
  ]);

  const selectedDataset = chartProperties?.properties[propKey]?.selectedDs;
  const oldSelectedDs = chartProperties?.properties[propKey]?.oldSelectedDs;
  const tablesForSelectedDataset = tabTileProps.tablesForSelectedDataSets;
  const selectedTable = chartProperties?.properties[propKey]?.selectedTable[selectedDataset?.id];
  const selectedTableInTablesForSelectedDataSets = tabTileProps?.tablesForSelectedDataSets[
    selectedDataset?.id
  ]?.findIndex((table: IndTabRilePropsTablesForSelectedDatasets) => table.id === selectedTable);
  const flatFileId =
    tabTileProps?.tablesForSelectedDataSets[selectedDataset?.id]?.[
      selectedTableInTablesForSelectedDataSets
    ]?.flatFileId;
  const currentCalculationSession = calculations?.properties[propKey]?.currentCalculationSession;

  let sampleRecords = JSON.parse(sessionStorage.getItem('sampleRecords') ?? '{}');

  const [tableRecords, setTableRecords] = useState(sampleRecords?.[dsId]?.[table] ?? null);
  const currentParameterSession = parameters?.properties[propKey]?.parameterSession;

  const formatFieldsData = () => {
    let _fieldsData: IFieldDataProps[] = [];
    if (tableRecords && tableRecords.length > 0) {
      var tableKeys = Object.keys(tableRecords[0]);
      var dataType = sampleRecords.recordsColumnType[dsId][table];

      for (let i = 0; i < tableKeys.length; i++) {
        // Check if the field is already present in saved calculations
        _fieldsData.push({
          fieldname: tableKeys[i],
          displayname:
            dataType?.filter(
              (sc: any) =>
                sc.columnName.toLowerCase().replace(/[^a-z0-9]/g, '_') ===
                tableKeys[i].toLowerCase(),
            )[0]?.columnName || tableKeys[i],
          dataType: dataType?.filter(
            (sc: any) =>
              sc.columnName.toLowerCase().replace(/[^a-z0-9]/g, '_') === tableKeys[i].toLowerCase(),
          )[0]?.dataType,
          tableId: table,
        });
      }
      return _fieldsData;
    }
    return _fieldsData;
  };

  const prepareInputs = () => {
    let _fields = formatFieldsData();

    _fields = _fields.sort((a, b) => {
      const isASaved = savedCalculations?.find((calc) => {
        return (
          calc.calculationInfo.calculatedFieldName?.toLowerCase().replace(/[^a-z0-9]/g, '') ===
            a.fieldname.toLowerCase().replace(/[^a-z0-9]/g, '') && !calc.isAggregated
        );
      });

      const isBSaved = savedCalculations?.find((calc) => {
        return (
          calc.calculationInfo.calculatedFieldName?.toLowerCase().replace(/[^a-z0-9]/g, '') ===
            b.fieldname.toLowerCase().replace(/[^a-z0-9]/g, '') && !calc.isAggregated
        );
      });

      if (isASaved && !isBSaved) return 1;

      if (!isASaved && isBSaved) return -1;

      return 0;
    });
    setColumnsData(_fields);
  };
  useEffect(() => {
    if (recordLoaded) {
      const raw = sessionStorage.getItem('sampleRecords');

      const sampleRecords = JSON.parse(raw ?? '{}');

      const data = sampleRecords?.[dsId]?.[table];
      if (data) {
        setTableRecords(data);
        setLoading(false);
      } else {
        setTableRecords(null);
      }
    }
  }, [recordLoaded]);

  useEffect(() => {
    if (!tableRecords) {
      let sampleRecords = JSON.parse(sessionStorage.getItem('sampleRecords') ?? '{}');
      const data = sampleRecords?.[dsId]?.[table];
      if (data) {
        setTableRecords(data);
        return;
      } else {
        setLoading(true);
      }
    } else {
      prepareInputs();
      setLoading(false);
    }
  }, [tableRecords]);

  useEffect(() => {
    let sampleRecords = JSON.parse(sessionStorage.getItem('sampleRecords') ?? '{}');
    const data = sampleRecords?.[dsId]?.[table];
    if (data) {
      setTableRecords(data);
    }
  }, [table]);

  const deleteItemFromChartFunc = (propKey: string, binIndex: number, itemIndex: number) =>
    dispatch(deleteItemInChartProp(propKey, binIndex, itemIndex));
  const deleteItemFromFilterGroupFunc = (groupKey: string, filterIndex: number) =>
    dispatch(deleteItemfromChartFilter(groupKey, filterIndex));
  const shouldScrollRef = useRef(false);

  useEffect(() => {
    const lastChild = tableRef.current?.lastElementChild;
    if (lastChild && shouldScrollRef.current) {
      lastChild.scrollIntoView({ behavior: 'smooth', inline: 'end' });
      shouldScrollRef.current = false;
    }
  }, [tableRecords]);

  useEffect(() => {
    const reloadSampleRecords = async () => {
      setLoading(true);
      let rowLevelcalculations = calculations?.savedCalculations
        ?.filter(
          (calculation) =>
            !calculation.isAggregated &&
            (calculation.datasetId === selectedDataset.id && calculation.tableId) === table,
        )
        .map((calculation) => calculation.calculationInfo);
      let tableRecordsResponse = await getTableData(
        selectedDataset.isExtract || selectedDataset.isFlatFileData,
        selectedDataset.connectionId,
        tabTileProps.tablesForSelectedDataSets[selectedDataset.id].find(
          (item: IndTabRilePropsTablesForSelectedDatasets) => item.id === table,
        ),
        selectedDataset.id,
        selectedDataset.workSpaceId,
        rowLevelcalculations,
        true,
      );

      let headersResponse = await getColumnTypes(
        selectedDataset.isExtract || selectedDataset.isFlatFileData,
        selectedDataset.connectionId,
        selectedDataset.id,
        tabTileProps.tablesForSelectedDataSets[selectedDataset.id].find(
          (item: IndTabRilePropsTablesForSelectedDatasets) => item.id === table,
        ),
        selectedDataset.workSpaceId,
        rowLevelcalculations,
        true,
      );

      if (
        (!tableRecordsResponse ||
          tableRecordsResponse.isError ||
          !tableRecordsResponse.data ||
          headersResponse.isError ||
          !headersResponse.data) &&
        tableRecordsResponse.statusCode === 422
      ) {
        let getMissingColumns: any[] = [];
        let getErrorCalculations: string[] = [];

        for (const cal of rowLevelcalculations) {
          const fieldKeys = Object.keys(cal.fields || {});
          const missingChecks = await Promise.all(
            fieldKeys.map(async (fieldKey: string) => {
              const res = await checkIfMissingColumn(
                cal.fields[fieldKey],
                selectedDs,
                tabTileProps.tablesForSelectedDataSets[selectedDs.id],
              );
              if (res.isMissing) {
                getMissingColumns.push({ [fieldKey]: cal.fields[fieldKey] });
                return true;
              }
              return false;
            }),
          );

          if (missingChecks.includes(true)) {
            getErrorCalculations.push(cal?.calculatedFieldName!);
          }
        }

        rowLevelcalculations = rowLevelcalculations.filter(
          (cal: any) => !getErrorCalculations.some((calc: any) => calc === cal.calculatedFieldName),
        );

        tableRecordsResponse = await getTableData(
          selectedDataset.isExtract || selectedDataset.isFlatFileData,
          selectedDataset.connectionId,
          tabTileProps.tablesForSelectedDataSets[selectedDataset.id].find(
            (item: IndTabRilePropsTablesForSelectedDatasets) => item.id === table,
          ),
          selectedDataset.id,
          selectedDataset.workSpaceId,
          rowLevelcalculations,
          true,
        );

        headersResponse = await getColumnTypes(
          selectedDataset.isExtract || selectedDataset.isFlatFileData,
          selectedDataset.connectionId,
          selectedDataset.id,
          tabTileProps.tablesForSelectedDataSets[selectedDataset.id].find(
            (item: IndTabRilePropsTablesForSelectedDatasets) => item.id === table,
          ),
          selectedDataset.workSpaceId,
          rowLevelcalculations,
          true,
        );

        const calculationHeaderResponse = getErrorCalculations.map((calc: any) => {
          const cal = calculations.savedCalculations.find(
            (cal: any) => !calc.isAggregated && cal.name === calc,
          );
          if (cal) {
            return {
              columnName: cal?.name,
              dataType: cal?.datatype,
            };
          }
        });

        headersResponse.data = [
          ...(headersResponse.data || []),
          ...(calculationHeaderResponse || []),
        ];

        if (Array.isArray(headersResponse.data) && tableRecordsResponse?.data) {
          for (const row of tableRecordsResponse.data) {
            for (const col of headersResponse.data) {
              if (row[col.columnName] === undefined) {
                row[col.columnName] = '';
              }
            }
          }
        }
      }
      if (
        (headersResponse.isError || !headersResponse.data) &&
        headersResponse.statusCode !== 422
      ) {
        setAlert({
          severity: 'error',
          message: headersResponse.message,
        });
        return;
      }
      if (
        (tableRecordsResponse.isError || !tableRecordsResponse.data) &&
        tableRecordsResponse.statusCode !== 422
      ) {
        setAlert({
          severity: 'error',
          message: tableRecordsResponse.message,
        });
        return;
      }

      sessionStorage.setItem(
        'sampleRecords',
        JSON.stringify(
          updateSampleRecords(
            sampleRecords,
            selectedDataset.id,
            tabTileProps.tablesForSelectedDataSets[selectedDataset.id].find(
              (item: IndTabRilePropsTablesForSelectedDatasets) => item.id === table,
            ).id,
            tableRecordsResponse.data,
            headersResponse.data,
          ),
        ),
      );
      setTableRecords(tableRecordsResponse.data);
      dispatch(resetFetchRowLevelCalc());
      setLoading(false);
      shouldScrollRef.current = true;
    };

    if (!didMountRef.current) {
      didMountRef.current = true; // Skip the API call on first run
      return;
    }

    if (calculations.fetchRowLevelCalculations || selectedDataset?.id !== oldSelectedDs?.id) {
      reloadSampleRecords();
    }
  }, [calculations.fetchRowLevelCalculations, selectedDataset?.id, oldSelectedDs?.id]);

  const isColumnMissing = async () => {
    let warnCalculations: {
      [key: string]: boolean;
    } = {};
    const savedCalculations = calculations?.savedCalculations || [];
    const promises = savedCalculations.map(async (calc: ICurrentCalculationSession) => {
      const fields = calc?.calculationInfo?.fields;
      if (!fields) return;
      const results = await Promise.all(
        Object.keys(fields).map((fieldKey) =>
          checkIfMissingColumn(
            fields[fieldKey],
            selectedDs,
            tabTileProps.tablesForSelectedDataSets[selectedDs.id],
          ),
        ),
      );
      warnCalculations = {
        ...warnCalculations,
        [calc.uuid!]: results.some((res) => res.isMissing),
      };
    });
    await Promise.all(promises);
    setWarningCalculation(warnCalculations);
  };

  useEffect(() => {
    isColumnMissing();
  }, [calculations?.savedCalculations, selectedDs.id, oldSelectedDs?.id]);

  // Get the column names from getKeys() and render the header for table
  const GetHeaders = () => {
    if (tableRecords && tableRecords.length > 0) {
      let keys = getKeys(tableRecords[0]);
      keys = keys.sort((a, b) => {
        const isASaved = savedCalculations?.find(
          (calc) =>
            calc.calculationInfo.calculatedFieldName?.toLowerCase().replace(/[^a-z0-9]/g, '') ===
              a.toLowerCase().replace(/[^a-z0-9]/g, '') && !calc.isAggregated,
        )
          ? true
          : false;
        const isBSaved = savedCalculations?.find(
          (calc) =>
            calc.calculationInfo.calculatedFieldName?.toLowerCase().replace(/[^a-z0-9]/g, '') ===
              b.toLowerCase().replace(/[^a-z0-9]/g, '') && !calc.isAggregated,
        )
          ? true
          : false;

        if (isASaved && !isBSaved) return 1;
        if (!isASaved && isBSaved) return -1;
        return 0;
      });
      return keys.map((key: string, index: number) => {
        const item = columnsData[index];

        const isSavedCalculation = savedCalculations.find((calc) => {
          // TODO: this has to be optimised, this is making everything super duper slow
          if (item && item.fieldname) {
            return (
              calc.calculationInfo.calculatedFieldName?.toLowerCase().replace(/[^a-z0-9]/g, '') ===
                item.fieldname.toLowerCase().replace(/[^a-z0-9]/g, '') && !calc.isAggregated
            );
          }
          return false;
        });

        let isCalculationPresentInChartAxes = false;
        let isCalculationPresentInFilterGroup = false;

        const informationForPropDeletion: {
          [key: string]: {
            binIndex: number;
            fieldIndex: number;
          }[];
        } = {};

        const informationForFilterDeletion: {
          [groupKey: string]: {
            filterIndex: number;
          }[];
        } = {};

        const allPropKeys = Object.keys(chartProperties?.properties);
        const allGroupKeys = Object.keys(chartGroup?.groups);

        for (const eachPropKey of allPropKeys) {
          chartProperties?.properties[eachPropKey]?.chartAxes?.forEach(
            // eslint-disable-next-line no-loop-func
            (ax: ChartPropChartAxes, axId: number) => {
              ax?.fields?.forEach((field: any, fieldId: number) => {
                // TODO: assumption is saved calculations have unique fieldname, so we can use that to check if the field is saved calculation or not
                if (field.SavedCalculationUUID && key === field.fieldname) {
                  if (!informationForPropDeletion[eachPropKey]) {
                    informationForPropDeletion[eachPropKey] = [];
                  }

                  informationForPropDeletion[eachPropKey].push({
                    binIndex: axId,
                    fieldIndex: fieldId,
                  });
                  isCalculationPresentInChartAxes = true;
                }
              });
            },
          );
        }

        for (const eachGroupKey of allGroupKeys) {
          // eslint-disable-next-line no-loop-func
          chartGroup?.groups[eachGroupKey]?.filters?.forEach((filter: any, filterId: number) => {
            // assumption: SavedCalculationUUID + fieldname uniquely identifies the calculation
            if (filter.SavedCalculationUUID && key === filter.fieldname) {
              if (!informationForFilterDeletion[eachGroupKey]) {
                informationForFilterDeletion[eachGroupKey] = [];
              }

              informationForFilterDeletion[eachGroupKey].push({
                filterIndex: filterId,
              });

              isCalculationPresentInFilterGroup = true;
            }
          });
        }

        const getDisplayName = (key: string) => {
          const normalizedKey = key.replace(/[^a-z0-9]/gi, '_').toUpperCase();
          const columnList = sampleRecords?.recordsColumnType?.[dsId]?.[table] || [];

          const match = columnList.find(
            (sc: any) => sc.columnName.replace(/[^a-z0-9]/gi, '_').toUpperCase() === normalizedKey,
          );

          return match?.columnName || key;
        };

        return (
          <TableCell key={`${index}_${key}`}>
            <Box
              allSavedCalculations={savedCalculations}
              name={getDisplayName(key)}
              type={isSavedCalculation ? 'calculation' : 'card'}
              fieldData={columnsData[index]}
              colsOnly={false}
              warning={warningCalculation[isSavedCalculation?.uuid ?? ''] ?? false}
              isSavedCalculation={isSavedCalculation}
              handleEditButton={() => {
                if (currentCalculationSession && currentCalculationSession.uuid) {
                  setAlert({
                    severity: 'warning',
                    message:
                      'Either save or close the current calculation before editing another calculation',
                  });
                  return;
                }
                if (currentParameterSession) {
                  setAlert({
                    severity: 'warning',
                    message:
                      'You have an unsaved parameter session open. Please save or close your current parameter session before editing or creating a calculation',
                  });
                  return;
                }
                dispatch(editSavedCalculation(propKey, columnsData[index].fieldname));
              }}
              informationForPropDeletion={informationForPropDeletion}
              informationForFilterDeletion={informationForFilterDeletion}
              isPresentInAxes={isCalculationPresentInChartAxes}
              isPresentInFilter={isCalculationPresentInFilterGroup}
              deleteIfPresentInAxes={(
                propKeyForThisDeleteInfo,
                binIndexForThisPropKey,
                fieldIndexForThisPropKey,
              ) => {
                const calculation = calculations.savedCalculations.find(
                  (calculation: any) => !calculation.isAggregated && calculation.name === key,
                );
                if (calculation) {
                  const savedParameters = parameters.savedParameters.find(
                    (param: any) =>
                      param.columns?.has_calculation &&
                      param.columns?.calculatedFieldId === calculation.uuid,
                  );
                  if (savedParameters) return;
                }

                deleteItemFromChartFunc(
                  propKeyForThisDeleteInfo,
                  binIndexForThisPropKey,
                  fieldIndexForThisPropKey,
                );
              }}
              deleteIfPresentInFilter={deleteItemFromFilterGroupFunc}
              handleDeleteButton={async () => {
                if (
                  currentCalculationSession &&
                  currentCalculationSession.uuid &&
                  key === currentCalculationSession.calculationInfo.calculatedFieldName
                ) {
                  setAlert({
                    severity: 'warning',
                    message:
                      'Either save or close the current calculation before deleting another calculation',
                  });
                  return;
                }
                const calculation = calculations.savedCalculations.find(
                  (calculation: any) => !calculation.isAggregated && calculation.name === key,
                );
                if (calculation) {
                  const savedParameters = parameters.savedParameters.find(
                    (param: any) =>
                      param.columns.has_calculation &&
                      param.columns.calculatedFieldId === calculation.uuid,
                  );
                  if (savedParameters) {
                    setAlert({
                      severity: 'warning',
                      message:
                        'This calculation is used in a parameter. Please remove the parameter before deleting the calculation.',
                    });
                    return;
                  }
                }

                // @ts-ignore
                const workspaceId = selectedDataset.workSpaceId;
                // @ts-ignore
                const databaseId = selectedDataset.connectionId;
                // @ts-ignore
                const datasetId = selectedDataset.id;

                const tableIdIndex = tablesForSelectedDataset[datasetId].findIndex(
                  (item: IndTabRilePropsTablesForSelectedDatasets) => item.id === selectedTable,
                );

                // @ts-ignore
                const database = tablesForSelectedDataset[datasetId][tableIdIndex].database;

                // @ts-ignore
                const schema = tablesForSelectedDataset[datasetId][tableIdIndex].schema;

                // @ts-ignore
                const tableId = tablesForSelectedDataset[datasetId][tableIdIndex].id;

                // @ts-ignore
                const tableName = tablesForSelectedDataset[datasetId][tableIdIndex].table;

                if (!columnsData[index].isAggregated) {
                  let allPreviousSavedNonAggregatedCalculations = [
                    ...calculations?.savedCalculations
                      ?.filter(
                        (calculation) =>
                          !calculation.isAggregated &&
                          calculation.datasetId === datasetId &&
                          calculation.tableId === tableId,
                      )
                      .map((calculation) => calculation.calculationInfo),
                  ];

                  const indexOfToBeDeletedCalculation =
                    allPreviousSavedNonAggregatedCalculations.findIndex((calc) => {
                      return (
                        calc.calculatedFieldName?.toLowerCase().replace(/[^a-z0-9]/g, '') ===
                        columnsData[index].fieldname.toLowerCase().replace(/[^a-z0-9]/g, '')
                      );
                    });

                  allPreviousSavedNonAggregatedCalculations.splice(
                    indexOfToBeDeletedCalculation,
                    1,
                  );

                  let allPreviousSavedNonAggregatedCalculationsArray =
                    allPreviousSavedNonAggregatedCalculations.map((cal) => [cal]);

                  if (allPreviousSavedNonAggregatedCalculationsArray.length > 0) {
                    const records = await FetchData({
                      url: flatFileId
                        ? `file-data-sample-records?datasetId=${datasetId}&tableId=${tableId}`
                        : `sample-records?workspaceId=${workspaceId}&databaseId=${databaseId}&datasetId=${datasetId}&recordCount=100&database=${database}&schema=${schema}&table=${tableName}&tableId=${tableId}`,
                      method: 'POST',
                      requestType: 'withData',
                      headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
                      },
                      data: allPreviousSavedNonAggregatedCalculationsArray,
                    });

                    const headers = await FetchData({
                      url: flatFileId
                        ? `file-data-column-details?workspaceId=${workspaceId}&tableId=${tableId}&datasetId=${datasetId}`
                        : `metadata-columns/${databaseId}?workspaceId=${workspaceId}&database=${database}&schema=${schema}&table=${tableName}`,
                      method: 'post',
                      requestType: 'withData',
                      headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
                      },
                      data: allPreviousSavedNonAggregatedCalculationsArray,
                    });

                    if (records.status === true && headers.status === true) {
                      const recordsData = await records.data;
                      const headersData: IGetMetaDataColumns[] = await headers.data.map(
                        (data: { fieldName?: string; columnName?: string; dataType: string }) => ({
                          columnName: flatFileId ? data.fieldName : data.columnName,
                          dataType: data.dataType,
                        }),
                      );
                      const newData = updateSampleRecords(
                        sampleRecords,
                        datasetId,
                        tableId,
                        recordsData,
                        headersData,
                      );
                      sessionStorage.setItem('sampleRecords', JSON.stringify(newData));
                      const specificTableRecords = newData?.[dsId]?.[table];
                      if (specificTableRecords) {
                        setTableRecords(specificTableRecords);
                      }
                    }
                  } else {
                    const records = await FetchData({
                      url: flatFileId
                        ? `file-data-sample-records?datasetId=${datasetId}&tableId=${tableId}`
                        : `sample-records?workspaceId=${workspaceId}&databaseId=${databaseId}&datasetId=${datasetId}&recordCount=100&database=${database}&schema=${schema}&table=${tableName}&tableId=${tableId}`,
                      headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
                      },
                      requestType: 'noData',
                      method: 'post',
                    });

                    const headers = await FetchData({
                      url: flatFileId
                        ? `file-data-column-details?workspaceId=${workspaceId}&tableId=${tableId}&datasetId=${datasetId}`
                        : `metadata-columns/${databaseId}?workspaceId=${workspaceId}&database=${database}&schema=${schema}&table=${tableName}`,
                      method: 'post',
                      headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
                      },
                      requestType: 'noData',
                    });

                    if (headers.status && records.status) {
                      const recordsData = await records.data;
                      const headersData = await headers.data.map(
                        (data: { fieldName?: string; columnName?: string; dataType: string }) => ({
                          columnName: flatFileId ? data.fieldName : data.columnName,
                          dataType: data.dataType,
                        }),
                      );
                      const newData = updateSampleRecords(
                        sampleRecords,
                        datasetId,
                        tableId,
                        recordsData,
                        headersData,
                      );
                      sessionStorage.setItem('sampleRecords', JSON.stringify(newData));
                      const specificTableRecords = newData?.[dsId]?.[table];
                      if (specificTableRecords) {
                        setTableRecords(specificTableRecords);
                      }
                    }
                  }
                }

                if (!calculation) {
                  dispatch(deleteSavedCalculation(columnsData[index].fieldname, propKey));
                  return;
                }

                const flows = Object.keys(calculation.calculationInfo.flows);
                flows.forEach((flow: any) => {
                  const flowName = calculation.calculationInfo.flows[flow][0].flow;
                  if (flowName === 'IfElse') {
                    const conditionFilters = calculation.calculationInfo.conditionFilters;
                    if (Object.keys(conditionFilters).length > 0) {
                      for (const conditionFilter in conditionFilters) {
                        conditionFilters[conditionFilter][0].conditions.forEach(
                          (condition: any) => {
                            const parameterIds = condition.parameterId;
                            if (parameterIds.length <= 0) return;
                            parameterIds.forEach((parameterId: string, index: number) => {
                              dispatch(removeCalculationId(calculation.uuid!, parameterId));
                            });
                          },
                        );
                      }
                    }
                  }
                });
                dispatch(deleteSavedCalculation(columnsData[index].fieldname, propKey));
              }}
            />
          </TableCell>
        );
      });
    } else return null;
  };
  const ColsOnly = () => {
    if (tableRecords && tableRecords.length > 0) {
      let keys = getKeys(tableRecords[0]);
      keys = keys.sort((a, b) => {
        const isASaved = savedCalculations?.find(
          (calc) =>
            calc.calculationInfo.calculatedFieldName?.toLowerCase().replace(/[^a-z0-9]/g, '') ===
              a.toLowerCase().replace(/[^a-z0-9]/g, '') && !calc.isAggregated,
        )
          ? true
          : false;
        const isBSaved = savedCalculations?.find(
          (calc) =>
            calc.calculationInfo.calculatedFieldName?.toLowerCase().replace(/[^a-z0-9]/g, '') ===
              b.toLowerCase().replace(/[^a-z0-9]/g, '') && !calc.isAggregated,
        )
          ? true
          : false;

        if (isASaved && !isBSaved) return 1;
        if (!isASaved && isBSaved) return -1;
        return a.localeCompare(b);
      });
      return keys.map((key: string, index: number) => {
        const item = columnsData[index];

        const isSavedCalculation = savedCalculations.find((calc) => {
          // TODO: this has to be optimised, this is making everything super duper slow
          if (item && item.fieldname) {
            return (
              calc.calculationInfo.calculatedFieldName?.toLowerCase().replace(/[^a-z0-9]/g, '') ===
                item.fieldname.toLowerCase().replace(/[^a-z0-9]/g, '') && !calc.isAggregated
            );
          }
          return false;
        });

        let isCalculationPresentInChartAxes = false;
        let isCalculationPresentInFilterGroup = false;

        const informationForPropDeletion: {
          [key: string]: {
            binIndex: number;
            fieldIndex: number;
          }[];
        } = {};

        const informationForFilterDeletion: {
          [groupKey: string]: {
            filterIndex: number;
          }[];
        } = {};

        const allPropKeys = Object.keys(chartProperties?.properties);
        const allGroupKeys = Object.keys(chartGroup?.groups);

        for (const eachPropKey of allPropKeys) {
          chartProperties?.properties[eachPropKey]?.chartAxes?.forEach(
            // eslint-disable-next-line no-loop-func
            (ax: ChartPropChartAxes, axId: number) => {
              ax?.fields?.forEach((field: any, fieldId: number) => {
                // TODO: assumption is saved calculations have unique fieldname, so we can use that to check if the field is saved calculation or not
                if (field.SavedCalculationUUID && key === field.fieldname) {
                  if (!informationForPropDeletion[eachPropKey]) {
                    informationForPropDeletion[eachPropKey] = [];
                  }

                  informationForPropDeletion[eachPropKey].push({
                    binIndex: axId,
                    fieldIndex: fieldId,
                  });
                  isCalculationPresentInChartAxes = true;
                }
              });
            },
          );
        }

        for (const eachGroupKey of allGroupKeys) {
          // eslint-disable-next-line no-loop-func
          chartGroup?.groups[eachGroupKey]?.filters?.forEach((filter: any, filterId: number) => {
            // assumption: SavedCalculationUUID + fieldname uniquely identifies the calculation
            if (filter.SavedCalculationUUID && key === filter.fieldname) {
              if (!informationForFilterDeletion[eachGroupKey]) {
                informationForFilterDeletion[eachGroupKey] = [];
              }

              informationForFilterDeletion[eachGroupKey].push({
                filterIndex: filterId,
              });

              isCalculationPresentInFilterGroup = true;
            }
          });
        }

        const getDisplayName = (key: string) => {
          const normalizedKey = key.replace(/[^a-z0-9]/gi, '_').toUpperCase();
          const columnList = sampleRecords.recordsColumnType[dsId]?.[table] || [];

          const match = columnList.find(
            (sc: any) => sc.columnName.replace(/[^a-z0-9]/gi, '_').toUpperCase() === normalizedKey,
          );

          return match?.columnName || key;
        };

        return (
          <button
            key={key}
            className="boxContainer"
            draggable="true"
            // onDragStart={(e) => handleDragStart(e, columnsData[index])}
          >
            <Box
              allSavedCalculations={savedCalculations}
              name={getDisplayName(key)}
              type={isSavedCalculation ? 'calculation' : 'card'}
              fieldData={columnsData[index]}
              colsOnly={true}
              isSavedCalculation={isSavedCalculation}
              warning={warningCalculation[isSavedCalculation?.uuid ?? ''] ?? false}
              handleEditButton={() => {
                if (currentCalculationSession && currentCalculationSession.uuid) {
                  setAlert({
                    severity: 'warning',
                    message:
                      'Either save or close the current calculation before editing another calculation',
                  });
                  return;
                }
                if (currentParameterSession) {
                  setAlert({
                    severity: 'warning',
                    message:
                      'You have an unsaved parameter session open. Please save or close your current parameter session before editing or creating a calculation',
                  });
                  return;
                }
                dispatch(editSavedCalculation(propKey, columnsData[index].fieldname));
              }}
              informationForPropDeletion={informationForPropDeletion}
              informationForFilterDeletion={informationForFilterDeletion}
              isPresentInAxes={isCalculationPresentInChartAxes}
              isPresentInFilter={isCalculationPresentInFilterGroup}
              deleteIfPresentInAxes={(
                propKeyForThisDeleteInfo,
                binIndexForThisPropKey,
                fieldIndexForThisPropKey,
              ) => {
                const calculation = calculations.savedCalculations.find(
                  (calculation: any) => !calculation.isAggregated && calculation.name === key,
                );
                if (calculation) {
                  const savedParameters = parameters.savedParameters.find(
                    (param: any) =>
                      param.columns?.has_calculation &&
                      param.columns?.calculatedFieldId === calculation.uuid,
                  );
                  if (savedParameters) return;
                }

                deleteItemFromChartFunc(
                  propKeyForThisDeleteInfo,
                  binIndexForThisPropKey,
                  fieldIndexForThisPropKey,
                );
              }}
              deleteIfPresentInFilter={deleteItemFromFilterGroupFunc}
              handleDeleteButton={async () => {
                if (
                  currentCalculationSession &&
                  currentCalculationSession.uuid &&
                  key === currentCalculationSession.calculationInfo.calculatedFieldName
                ) {
                  setAlert({
                    severity: 'warning',
                    message:
                      'Either save or close the current calculation before deleting another calculation',
                  });
                  return;
                }
                const calculation = calculations.savedCalculations.find(
                  (calculation: any) => !calculation.isAggregated && calculation.name === key,
                );
                if (calculation) {
                  const savedParameters = parameters.savedParameters.find(
                    (param: any) =>
                      param.columns.has_calculation &&
                      param.columns.calculatedFieldId === calculation.uuid,
                  );
                  if (savedParameters) {
                    setAlert({
                      severity: 'warning',
                      message:
                        'This calculation is used in a parameter. Please remove the parameter before deleting the calculation.',
                    });
                    return;
                  }
                }

                // @ts-ignore
                const workspaceId = selectedDataset.workSpaceId;
                // @ts-ignore
                const databaseId = selectedDataset.connectionId;
                // @ts-ignore
                const datasetId = selectedDataset.id;

                const tableIdIndex = tablesForSelectedDataset[datasetId].findIndex(
                  (item: IndTabRilePropsTablesForSelectedDatasets) => item.id === selectedTable,
                );

                // @ts-ignore
                const database = tablesForSelectedDataset[datasetId][tableIdIndex].database;

                // @ts-ignore
                const schema = tablesForSelectedDataset[datasetId][tableIdIndex].schema;

                // @ts-ignore
                const tableId = tablesForSelectedDataset[datasetId][tableIdIndex].id;

                // @ts-ignore
                const tableName = tablesForSelectedDataset[datasetId][tableIdIndex].table;

                if (!columnsData[index].isAggregated) {
                  let allPreviousSavedNonAggregatedCalculations = [
                    ...calculations?.savedCalculations
                      ?.filter(
                        (calculation) =>
                          !calculation.isAggregated &&
                          calculation.datasetId === datasetId &&
                          calculation.tableId === tableId,
                      )
                      .map((calculation) => calculation.calculationInfo),
                  ];

                  const indexOfToBeDeletedCalculation =
                    allPreviousSavedNonAggregatedCalculations.findIndex((calc) => {
                      return (
                        calc.calculatedFieldName?.toLowerCase().replace(/[^a-z0-9]/g, '') ===
                        columnsData[index].fieldname.toLowerCase().replace(/[^a-z0-9]/g, '')
                      );
                    });

                  allPreviousSavedNonAggregatedCalculations.splice(
                    indexOfToBeDeletedCalculation,
                    1,
                  );

                  let allPreviousSavedNonAggregatedCalculationsArray =
                    allPreviousSavedNonAggregatedCalculations.map((cal) => [cal]);

                  if (allPreviousSavedNonAggregatedCalculationsArray.length > 0) {
                    for (const eachCalculation of allPreviousSavedNonAggregatedCalculationsArray) {
                      const calculationWhereFlowHasToBeSorted = eachCalculation[0];

                      const dependencyTracker: {
                        [key: string]: boolean;
                      } = {};

                      for (const flow in calculationWhereFlowHasToBeSorted.flows) {
                        dependencyTracker[flow] = false;
                      }

                      const sortedDependencyStack: any = [];
                      let cycleDetected = false; // Add this flag to track if a cycle was detected

                      const flowSorter = (flow: string, visited: Set<string>) => {
                        // Early return if a cycle was already detected
                        if (cycleDetected) return;

                        if (sortedDependencyStack.includes(flow)) return;
                        if (visited.has(flow)) {
                          setAlert({
                            severity: 'warning',
                            message: 'There is a cycle in the formula',
                          });
                          cycleDetected = true; // Set the flag when a cycle is detected
                          return;
                        }

                        visited.add(flow);
                        dependencyTracker[flow] = true;

                        for (const condition of calculationWhereFlowHasToBeSorted.flows[flow]) {
                          // Check early if cycle was detected in a recursive call
                          if (cycleDetected) return;

                          const sourceList = condition.source.filter(
                            (source: string, index: number) =>
                              condition.sourceType[index] === 'flow',
                          );

                          if (condition.flow === 'IfElse') {
                            const filterId = condition.filter;
                            const filter =
                              calculationWhereFlowHasToBeSorted.conditionFilters[filterId!][0];

                            for (const leftOperand of filter.conditions) {
                              const leftOperandType = leftOperand.leftOperandType[0];
                              if (
                                leftOperandType === 'flow' &&
                                !sourceList.includes(leftOperand.leftOperand[0])
                              ) {
                                sourceList.push(leftOperand.leftOperand[0]);
                              }

                              const rightOperandType = leftOperand.rightOperandType?.[0];
                              if (
                                rightOperandType === 'flow' &&
                                !sourceList.includes(leftOperand.rightOperand?.[0])
                              ) {
                                sourceList.push(leftOperand.rightOperand?.[0]);
                              }
                            }
                          }

                          for (const source of sourceList) {
                            flowSorter(source, new Set(visited)); // Pass a new set to avoid mutation issues
                            // Check after each recursive call
                            if (cycleDetected) return;
                          }
                        }

                        visited.delete(flow);

                        if (!sortedDependencyStack.includes(flow)) {
                          sortedDependencyStack.push(flow);
                        }
                      };

                      for (const flow in dependencyTracker) {
                        if (cycleDetected) break; // Stop processing if a cycle was detected
                        if (!sortedDependencyStack.includes(flow)) {
                          flowSorter(flow, new Set());
                        }
                      }

                      if (cycleDetected) {
                        setAlert({
                          severity: 'warning',
                          message: `Cyclic dependency detected. Please correct the formula.`,
                        });
                        return;
                      }

                      const newSortedFlows: any = {};

                      for (const sortedDependency of sortedDependencyStack) {
                        newSortedFlows[sortedDependency] =
                          calculationWhereFlowHasToBeSorted.flows[sortedDependency];
                      }

                      eachCalculation[0].flows = newSortedFlows;
                    }

                    const records = await FetchData({
                      url: flatFileId
                        ? `file-data-sample-records?datasetId=${datasetId}&tableId=${tableId}`
                        : `sample-records?workspaceId=${workspaceId}&databaseId=${databaseId}&datasetId=${datasetId}&recordCount=100&database=${database}&schema=${schema}&table=${tableName}&tableId=${tableId}`,
                      method: 'POST',
                      requestType: 'withData',
                      headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
                      },
                      data: allPreviousSavedNonAggregatedCalculationsArray,
                    });

                    const headers = await FetchData({
                      url: flatFileId
                        ? `file-data-column-details?workspaceId=${workspaceId}&tableId=${tableId}&datasetId=${datasetId}`
                        : `metadata-columns/${databaseId}?workspaceId=${workspaceId}&database=${database}&schema=${schema}&table=${tableName}`,
                      method: 'post',
                      requestType: 'withData',
                      headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
                      },
                      data: allPreviousSavedNonAggregatedCalculationsArray,
                    });

                    if (records.status === true && headers.status === true) {
                      const recordsData = await records.data;
                      const headersData: IGetMetaDataColumns[] = await headers.data.map(
                        (data: { fieldName?: string; columnName?: string; dataType: string }) => ({
                          columnName: flatFileId ? data.fieldName : data.columnName,
                          dataType: data.dataType,
                        }),
                      );
                      
                      const newData = updateSampleRecords(
                        sampleRecords,
                        datasetId,
                        tableId,
                        recordsData,
                        headersData,
                      );
                      sessionStorage.setItem('sampleRecords', JSON.stringify(newData));
                      const specificTableRecords = newData?.[dsId]?.[table];
                      if (specificTableRecords) {
                        setTableRecords(specificTableRecords);
                      }
                    }
                  } else {
                    const records = await FetchData({
                      url: flatFileId
                        ? `file-data-sample-records?flatfileId=${flatFileId}&datasetId=${datasetId}&tableId=${tableId}`
                        : `sample-records?workspaceId=${workspaceId}&databaseId=${databaseId}&datasetId=${datasetId}&recordCount=100&database=${database}&schema=${schema}&table=${tableName}&tableId=${tableId}`,
                      headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
                      },
                      requestType: 'noData',
                      method: 'post',
                    });

                    const headers = await FetchData({
                      url: flatFileId
                        ? `file-data-column-details?workspaceId=${workspaceId}&tableId=${tableId}&datasetId=${datasetId}`
                        : `metadata-columns/${databaseId}?workspaceId=${workspaceId}&database=${database}&schema=${schema}&table=${tableName}`,
                      method: 'post',
                      headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
                      },
                      requestType: 'noData',
                    });

                    if (headers.status && records.status) {
                      const recordsData = await records.data;
                      const headersData = await headers.data.map(
                        (data: { fieldName?: string; columnName?: string; dataType: string }) => ({
                          columnName: flatFileId ? data.fieldName : data.columnName,
                          dataType: data.dataType,
                        }),
                      );
                      
                      const newData = updateSampleRecords(
                        sampleRecords,
                        datasetId,
                        tableId,
                        recordsData,
                        headersData,
                      );
                      sessionStorage.setItem('sampleRecords', JSON.stringify(newData));
                      const specificTableRecords = newData?.[dsId]?.[table];
                      if (specificTableRecords) {
                        setTableRecords(specificTableRecords);
                      }
                    }
                  }
                }

                if (!calculation) {
                  dispatch(deleteSavedCalculation(columnsData[index].fieldname, propKey));
                  return;
                }

                const flows = Object.keys(calculation.calculationInfo.flows);
                flows.forEach((flow: any) => {
                  const flowName = calculation.calculationInfo.flows[flow][0].flow;
                  if (flowName === 'IfElse') {
                    const conditionFilters = calculation.calculationInfo.conditionFilters;
                    if (Object.keys(conditionFilters).length > 0) {
                      for (const conditionFilter in conditionFilters) {
                        conditionFilters[conditionFilter][0].conditions.forEach(
                          (condition: any) => {
                            const parameterIds = condition.parameterId;
                            if (parameterIds.length <= 0) return;
                            parameterIds.forEach((parameterId: string, index: number) => {
                              dispatch(removeCalculationId(calculation.uuid!, parameterId));
                            });
                          },
                        );
                      }
                    }
                  }
                });
                dispatch(deleteSavedCalculation(columnsData[index].fieldname, propKey));
              }}
            />
          </button>
        );
      });
    } else return null;
  };

  return (
    <>
      {alert && (
        <NotificationDialog
          severity={alert.severity}
          openAlert={!!alert}
          onCloseAlert={() => setAlert(null)}
          testMessage={alert.message}
        />
      )}
      {tabTileProps.columnsOnlyDisplay && tableRecords && tableRecords.length > 0 ? (
        <div className="showColumnsOnly">{ColsOnly()}</div>
      ) : (
        <TableContainer
          ref={tableRef}
          sx={{
            height: '100%',
            overflow: 'hidden',
            paddingLeft: '10px',
            '&:hover': {
              overflow: 'auto',
            },
            '::-webkit-scrollbar': {
              width: '5px',
              height: '5px',
            },
          }}
        >
          {!tableRecords && loading ? (
            <div className="loading-container" style={{ height: '100%' }}>
              <div className="spinner"></div>
            </div>
          ) : tableRecords && tableRecords.length > 0 ? (
            <Table stickyHeader={true} sx={{ width: 'fit-content' }}>
              <TableHead
                sx={{
                  '& .MuiTableCell-root': {
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    color: 'primary.main',
                    padding: '2px 9px 7px 0px ',
                    backgroundColor: 'white',
                    lineHeight: 'normal',
                    letterSpacing: 'normal',
                    fontFamily:
                      " -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;",
                  },
                }}
              >
                <TableRow
                  sx={{
                    '& .MuiTableCell-root': {
                      borderBottom: '0px',
                    },
                  }}
                >
                  {GetHeaders()}
                </TableRow>
              </TableHead>
              <TableBody
                sx={{
                  '& .MuiTableCell-root': {
                    fontSize: '0.75rem',
                    padding: '0px 10px 0px 25px ',
                    whiteSpace: 'nowrap',
                    maxWidth: '250px',
                    minWidth: '75px',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    backgroundColor: 'white',
                    color: 'primary.contrastText',
                    fontFamily:
                      "  'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;",
                  },
                }}
              >
                {getRowsData(tableRecords, savedCalculations)}
              </TableBody>
            </Table>
          ) : loading ? (
            <LoadingPopover />
          ) : (
            <div
              className="loading-container"
              style={{
                height: '100%',
                width: '100%',
                border: '1px solid white',
                overflow: 'hidden',
                marginTop: '0.5rem',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              No records are available for this table
            </div>
          )}
        </TableContainer>
      )}
    </>
  );
};

export default DisplayTable;
